import React from "react";

import { calculateMaxEstimatedCost } from "ComparePlans/calculateCost";
import { getDependents, getEmployeeForm } from "EmployeeApplication/api";
import { useMedicalContext } from "EmployeeCoverage/Components/Medical/medical.context";
import RecommendationModal from "EmployeeCoverage/Components/RecommendationModal";
import { NETWORK_DESCRIPTIONS } from "QuotePage/NetworkDescriptions";
import { useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import formatCurrency, { notCoveredOrFormatCurrency } from "Utils/formatCurrency";
import { formatNameFL } from "Utils/formatName";
import getTooltip from "Utils/getTooltip";

import BodyText from "Cobalt/BodyText";
import { CardSection } from "Cobalt/Card";
import { StandaloneTrigger } from "Cobalt/Modal";
import { Popover } from "Cobalt/Popover";
import { HeaderTR, Table, TD, TH, TR } from "Cobalt/TableV2";
import CarrierLogo from "Common/CarrierLogo";
import FinePrint from "Common/FinePrint";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import InfoPopover from "Common/InfoPopover";
import { SizeSwitch } from "Common/Responsive";
import SIOverlay from "Common/SIOverlay";
import SIOverlayTrigger from "Common/SIOverlayTrigger";
import Button from "Theme/Button";
import { CardHeader } from "Theme/Card";

import { MedicalEnrollOrDeclineButton } from "./medical.modals";

import type { MedicalPlan, PlanCost } from "Utils/types";

interface Props {
  plan: MedicalPlan;
  planCost: PlanCost;
}

const calculateDisplayValues = (individual, family) => {
  if (!family) {
    return `${formatCurrency(individual)} per individual, ${formatCurrency(individual * 2)} per family`;
  }
  return `${formatCurrency(individual)} per individual, ${formatCurrency(family)} per family`;
};

const PlanTermLabelPopover = ({ label }) => (
  <SIOverlayTrigger
    placement="bottom"
    overlay={
      <Popover showBackdrop={false} className="plan-term-tooltip" id={label}>
        {getTooltip(label)}
      </Popover>
    }
    trackType="dashed-plan-term"
    trackId={label}
  >
    <span className="SIOverlayTrigger__base">{label}</span>
  </SIOverlayTrigger>
);

const PlanOverviewTable = ({ plan }) => (
  <Table>
    <thead>
      <HeaderTR>
        <TH>Feature</TH>
        <TH align="left" width="50%">
          Detail
        </TH>
      </HeaderTR>
    </thead>
    <tbody>
      {["TX", "CA", "FL", "NY", "VA", "MD", "GA", "NJ", "IL", "AZ"].includes(plan.state) && (
        <TR highlightFirstCell={false} withBorder={false}>
          <TD>Plan Network</TD>
          <TD align="left">{plan.fine_print.network || plan.network}</TD>
        </TR>
      )}
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>Network Type</TD>
        <TD align="left">
          <SIOverlay
            placement="bottom"
            overlayContent={
              <Popover showBackdrop={false} className="plan-term-tooltip" id={plan.fine_print.plan_type || plan.plan_type}>
                {NETWORK_DESCRIPTIONS[plan.fine_print.plan_type || plan.plan_type]}
              </Popover>
            }
            overlayTarget={<span className="SIOverlayTrigger__base">{plan.fine_print.plan_type || plan.plan_type}</span>}
            trackType="dashed-plan-term"
            trackId={plan.fine_print.plan_type || plan.plan_type}
          />
        </TD>
      </TR>
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>
          <PlanTermLabelPopover label="Deductible" />
        </TD>
        <TD align="left">{calculateDisplayValues(plan.deductible, plan.family_deductible)}</TD>
      </TR>
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>
          <PlanTermLabelPopover label="Annual Out-of-Pocket Limit" />
        </TD>
        <TD align="left">{calculateDisplayValues(plan.limit, plan.family_limit)}</TD>
      </TR>
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>
          <PlanTermLabelPopover label="Estimated Doctor Visit" />
        </TD>
        <TD align="left">{formatCurrency(plan.estimated_costs.pcp_visit)}</TD>
      </TR>
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>
          <PlanTermLabelPopover label="Estimated Prescription Drugs" />
        </TD>
        <TD align="left">
          {formatCurrency(plan.estimated_costs.generic_drugs)} - {formatCurrency(plan.estimated_costs.brand_drugs)}
        </TD>
      </TR>
      <TR highlightFirstCell={false} withBorder={false}>
        <TD>
          <PlanTermLabelPopover label="Estimated Hospitalization" />
        </TD>
        <TD align="left">{formatCurrency(calculateMaxEstimatedCost(plan, "hospitalization"))}</TD>
      </TR>
    </tbody>
  </Table>
);

const MECPlanOverviewTable = ({ plan }) => {
  const drugTier1 = notCoveredOrFormatCurrency(plan.estimated_costs["drug_tier:Tier 1"]);

  const drugTier4 = notCoveredOrFormatCurrency(plan.estimated_costs["drug_tier:Tier 4"]);

  const planDetails = {
    Deductible: notCoveredOrFormatCurrency(plan.deductible),
    "Out of Pocket Maximum": plan.limit === 999999 ? "Unlimited" : notCoveredOrFormatCurrency(plan.limit),
    "Preventative Care Visits": "$0 Copay",
    "Preventative Care Prescriptions": "$0 Copay",
    "Primary Care Visits": notCoveredOrFormatCurrency(plan.estimated_costs.pcp_visit),

    "Specialist Visits": notCoveredOrFormatCurrency(plan.estimated_costs.specialist_visit),

    "Urgent Care": notCoveredOrFormatCurrency(plan.estimated_costs.urgent_care),
    "Other Prescription Coverage": drugTier1 === "Not Covered" ? drugTier1 : `${drugTier1} - ${drugTier4}`,
    "ER and Hospital": notCoveredOrFormatCurrency(plan.estimated_costs.emergency_room),
  };
  return (
    <Table>
      <tbody>
        {Object.entries(planDetails).map(([name, value]) => (
          <TR key={name}>
            <TD>{name}</TD>
            <TD>{value}</TD>
          </TR>
        ))}
      </tbody>
    </Table>
  );
};

export const MedicalSinglePlan = ({ plan, planCost }: Props) => {
  const employeeForm = useSelector(getEmployeeForm);
  const dependents = useSelector(getDependents);
  const spousesOrDomesticPartners = dependents
    .filter((d) => d.dependentType === "spouse" || d.dependentType === "domestic_partner")
    .sort(({ bornAt: a }, { bornAt: b }) => a - b);
  const children = dependents.filter((d) => d.dependentType === "child").sort(({ bornAt: a }, { bornAt: b }) => a - b);
  const breakdown = planCost.pricingType === "age" ? planCost.costBreakdowns.age : planCost.costBreakdowns.eeOnly;
  const { openEnrollmentModal } = useMedicalContext();

  return (
    <>
      <CardHeader>Your Medical Plan Choices</CardHeader>
      <CardSection>
        <Table>
          <tbody>
            <TR highlightFirstCell={false} withBorder={false}>
              <TD>
                <CarrierLogo plan={plan} maxSize={3} />
              </TD>
              <TD align="left" width="50%">
                <BodyText>{plan.display_name}</BodyText>
              </TD>
            </TR>
          </tbody>
        </Table>
      </CardSection>
      <CardSection>
        <BodyText level={6} mb={4}>
          Premium Overview
        </BodyText>
        <Table>
          <thead>
            <HeaderTR>
              <TH>Person</TH>
              <TH align="left" width="50%">
                Your Cost
                <InfoPopover
                  infoTitle="Payroll Contributions"
                  infoText="This is the amount you pay each month, taken directly out of your paycheck."
                  placement="top"
                />
              </TH>
            </HeaderTR>
          </thead>
          <tbody>
            {planCost.pricingType === "age" && (
              <>
                <TR highlightFirstCell={false} withBorder={false}>
                  <TD>{formatNameFL(employeeForm)}</TD>
                  <TD align="left">{formatCurrency(breakdown.employeeCost)}/mo</TD>
                </TR>
                {spousesOrDomesticPartners.map((spouse) => (
                  <TR highlightFirstCell={false} withBorder={false} key={spouse.id}>
                    <TD>{formatNameFL(spouse)}</TD>
                    <TD align="left">{formatCurrency(planCost.pricing[spouse.id])}/mo</TD>
                  </TR>
                ))}
                {children.map((child) => (
                  <TR highlightFirstCell={false} withBorder={false} key={child.id}>
                    <TD>{formatNameFL(child)}</TD>
                    <TD align="left">{formatCurrency(planCost.pricing[child.id])}/mo</TD>
                  </TR>
                ))}
              </>
            )}
            {planCost.pricingType === "segment" && (
              <>
                {planCost.costBreakdowns.eeOnly && (
                  <TR highlightFirstCell={false} withBorder={false}>
                    <TD>Just Me</TD>
                    <TD align="left">{formatCurrency(planCost.costBreakdowns.eeOnly.employeeTotalCost)}/mo</TD>
                  </TR>
                )}
                {planCost.costBreakdowns.eeSpouse && (
                  <TR highlightFirstCell={false} withBorder={false}>
                    <TD>Me and My Spouse</TD>
                    <TD align="left">{formatCurrency(planCost.costBreakdowns.eeSpouse.employeeTotalCost)}/mo</TD>
                  </TR>
                )}
                {planCost.costBreakdowns.eeChildren && (
                  <TR highlightFirstCell={false} withBorder={false}>
                    <TD>Me and My Children</TD>
                    <TD align="left">{formatCurrency(planCost.costBreakdowns.eeChildren.employeeTotalCost)}/mo</TD>
                  </TR>
                )}
                {planCost.costBreakdowns.eeFamily && (
                  <TR highlightFirstCell={false} withBorder={false}>
                    <TD>Me, My Spouse, and My Children</TD>
                    <TD align="left">{formatCurrency(planCost.costBreakdowns.eeFamily.employeeTotalCost)}/mo</TD>
                  </TR>
                )}
              </>
            )}
            <TR highlightFirstCell={false} withBorder={false}>
              <TD>&nbsp;</TD>
              <TD align="left">
                <MedicalEnrollOrDeclineButton plan={plan} planCost={planCost} />
              </TD>
            </TR>
          </tbody>
        </Table>
      </CardSection>
      <CardSection>
        <BodyText level={6} mb={4}>
          Plan Overview
        </BodyText>
        {plan.target_market === "mec" && <MECPlanOverviewTable plan={plan} />}
        {plan.target_market !== "mec" && (
          <>
            <PlanOverviewTable plan={plan} />
            <Div mt={3}>
              <SizeSwitch
                md={
                  <div>
                    <FinePrint plan={plan} showColoredDots={false} />
                  </div>
                }
                xs={<FinePrint plan={plan} isMobile showColoredDots={false} />}
              />
            </Div>
          </>
        )}
        {plan.has_sbc && (
          <Div mt="3">
            <a href={plan.sbc_url} target="_blank" rel="noreferrer">
              <Icon flavor="solid" icon="download" mr={3} />
              View Plan Summary of Benefits and Coverage
            </a>
          </Div>
        )}
      </CardSection>
    </>
  );
};
